<!--
  OnboardingTour.vue

  Renders the guided coachmark tour. Driven by `useOnboardingStore`. For each
  step it finds the anchored element ([data-tour="…"]), dims the rest of the
  screen with a spotlight cut-out, and floats a tooltip card next to it. Steps
  without a target render as a centered welcome/finish modal.

  Mounted once at the app root (App.vue) and teleported to <body>.
-->
<template>
  <Teleport to="body">
    <div v-if="store.isActive" class="onb" role="dialog" aria-modal="true" aria-label="Product tour">
      <!-- Click-blocker: dims fully for centered steps, transparent when a spotlight is shown -->
      <div class="onb-catch" :class="{ 'onb-catch--dim': !targetRect }" @click.self="noop" />

      <!-- Spotlight cut-out + highlight ring around the anchored element -->
      <div v-if="targetRect" class="onb-spotlight" :style="spotlightStyle" />

      <!-- Tooltip / modal card -->
      <div
        class="onb-card"
        :class="[`onb-card--${cardPlacement}`, { 'onb-card--center': !targetRect }]"
        :style="cardStyle"
      >
        <button class="onb-skip" type="button" @click="onSkip">Skip tour</button>

        <p v-if="step?.eyebrow" class="onb-eyebrow">{{ step.eyebrow }}</p>
        <h2 class="onb-title">{{ step?.title }}</h2>
        <p class="onb-body">{{ step?.body }}</p>

        <div class="onb-footer">
          <div class="onb-dots" aria-hidden="true">
            <span
              v-for="(s, i) in store.steps"
              :key="s.id"
              class="onb-dot"
              :class="{ 'onb-dot--active': i === store.currentIndex }"
            />
          </div>
          <div class="onb-actions">
            <button v-if="!store.isFirst" class="onb-btn onb-btn--ghost" type="button" @click="store.prev()">
              Back
            </button>
            <button class="onb-btn onb-btn--primary" type="button" @click="onNext">
              {{ store.isLast ? 'Get cooking' : 'Next' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useOnboardingStore } from '../../stores/onboarding'

const store = useOnboardingStore()
const router = useRouter()
const route = useRoute()

const step = computed(() => store.currentStep)

// Bounding rect of the currently anchored element (null for centered steps).
const targetRect = ref<DOMRect | null>(null)
// Resolved placement (may flip from the step's preference if there isn't room).
const cardPlacement = computed(() => step.value?.placement ?? 'center')

const noop = () => {}

const onNext = () => store.next()
const onSkip = () => store.dismiss()

/** Poll for an element to appear (it may mount after a route change). */
function waitForElement(selector: string, timeout = 2500): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const found = document.querySelector<HTMLElement>(selector)
    if (found) return resolve(found)
    const start = performance.now()
    const tick = () => {
      const el = document.querySelector<HTMLElement>(selector)
      if (el) return resolve(el)
      if (performance.now() - start > timeout) return resolve(null)
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })
}

function updateRect() {
  const sel = step.value?.target
  if (!sel) {
    targetRect.value = null
    return
  }
  const el = document.querySelector<HTMLElement>(sel)
  const rect = el?.getBoundingClientRect() ?? null
  // Treat hidden/zero-area anchors (e.g. the desktop nav on mobile) as "no target"
  // so the step falls back to a centered card instead of a broken spotlight.
  targetRect.value = rect && rect.width > 0 && rect.height > 0 ? rect : null
}

/** Ensure we're on the right route, find the target, scroll it into view. */
async function locateTarget() {
  const s = step.value
  if (!s) return

  if (s.route && route.path !== s.route) {
    await router.push(s.route)
    await nextTick()
  }

  if (!s.target) {
    targetRect.value = null
    return
  }

  const el = await waitForElement(s.target)
  if (!el) {
    targetRect.value = null // gracefully fall back to a centered card
    return
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
  // Recompute a few times while the smooth scroll settles.
  updateRect()
  for (const delay of [120, 280, 460]) {
    window.setTimeout(updateRect, delay)
  }
}

// Spotlight box: sits over the target, its giant box-shadow dims everything else.
const PAD = 8
const spotlightStyle = computed(() => {
  const r = targetRect.value
  if (!r) return {}
  return {
    top: `${r.top - PAD}px`,
    left: `${r.left - PAD}px`,
    width: `${r.width + PAD * 2}px`,
    height: `${r.height + PAD * 2}px`,
  }
})

// Tooltip card position relative to the target (centered steps use CSS instead).
const cardStyle = computed(() => {
  const r = targetRect.value
  if (!r) return {}
  const gap = 14
  const margin = 16
  const cardW = Math.min(360, window.innerWidth - margin * 2)
  const centerX = r.left + r.width / 2
  const clampedLeft = Math.max(margin, Math.min(centerX - cardW / 2, window.innerWidth - margin - cardW))
  const style: Record<string, string> = { width: `${cardW}px` }

  switch (cardPlacement.value) {
    case 'top':
      style.bottom = `${window.innerHeight - r.top + gap}px`
      style.left = `${clampedLeft}px`
      break
    case 'left':
      style.right = `${window.innerWidth - r.left + gap}px`
      style.top = `${r.top + r.height / 2}px`
      style.transform = 'translateY(-50%)'
      break
    case 'right':
      style.left = `${r.right + gap}px`
      style.top = `${r.top + r.height / 2}px`
      style.transform = 'translateY(-50%)'
      break
    case 'bottom':
    default:
      style.top = `${r.bottom + gap}px`
      style.left = `${clampedLeft}px`
      break
  }
  return style
})

// Keep the spotlight/card glued to the target as the page scrolls or resizes.
const onViewportChange = () => updateRect()
const onKeydown = (e: KeyboardEvent) => {
  if (!store.isActive) return
  if (e.key === 'Escape') onSkip()
  else if (e.key === 'ArrowRight') onNext()
  else if (e.key === 'ArrowLeft' && !store.isFirst) store.prev()
}

function attachListeners() {
  window.addEventListener('scroll', onViewportChange, true)
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('keydown', onKeydown)
}
function detachListeners() {
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('keydown', onKeydown)
}

watch(
  () => store.isActive,
  (active) => {
    if (active) {
      attachListeners()
      locateTarget()
    } else {
      detachListeners()
      targetRect.value = null
    }
  },
  { immediate: true },
)

watch(
  () => store.currentIndex,
  () => {
    if (store.isActive) locateTarget()
  },
)

onBeforeUnmount(detachListeners)
</script>

<style scoped>
.onb {
  position: fixed;
  inset: 0;
  z-index: 4000;
  font-family: 'Inter', sans-serif;
}

/* Full-screen click blocker. Transparent when a spotlight dims the page itself. */
.onb-catch {
  position: fixed;
  inset: 0;
  background: transparent;
}
.onb-catch--dim {
  background: rgba(15, 23, 42, 0.55);
}

/* The spotlight: its outsized box-shadow produces the dimmed surround + cut-out. */
.onb-spotlight {
  position: fixed;
  border-radius: 14px;
  box-shadow:
    0 0 0 9999px rgba(15, 23, 42, 0.55),
    0 0 0 3px var(--color-primary-500, #4f8646);
  pointer-events: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.onb-card {
  position: fixed;
  z-index: 4001;
  width: 360px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.25);
  animation: onb-pop 0.22s ease both;
}

.onb-card--center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  padding: 28px 26px;
}

@keyframes onb-pop {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
}
/* Centered card keeps its own transform during the pop. */
.onb-card--center {
  animation: onb-pop-center 0.22s ease both;
}
@keyframes onb-pop-center {
  from {
    opacity: 0;
    transform: translate(-50%, -46%) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.onb-skip {
  position: absolute;
  top: 14px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  font-family: inherit;
}
.onb-skip:hover {
  color: #64748b;
}
.onb-card--center .onb-skip {
  top: 16px;
  right: 18px;
}

.onb-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-primary-500, #4f8646);
  margin: 0 0 4px;
}

.onb-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
  letter-spacing: -0.2px;
}

.onb-body {
  font-size: 13.5px;
  line-height: 1.55;
  color: #475569;
  margin: 0 0 18px;
}

.onb-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.onb-card--center .onb-footer {
  flex-direction: column;
  gap: 16px;
}

.onb-dots {
  display: flex;
  gap: 6px;
}
.onb-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e2e8f0;
  transition: all 0.2s;
}
.onb-dot--active {
  background: var(--color-primary-500, #4f8646);
  width: 18px;
  border-radius: 3px;
}

.onb-actions {
  display: flex;
  gap: 8px;
}

.onb-btn {
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  padding: 9px 16px;
  transition: all 0.15s;
}
.onb-btn--ghost {
  background: #f1f5f9;
  color: #475569;
}
.onb-btn--ghost:hover {
  background: #e2e8f0;
}
.onb-btn--primary {
  background: var(--color-primary-500, #4f8646);
  color: #ffffff;
}
.onb-btn--primary:hover {
  background: var(--color-primary-600, #3f6e38);
}
</style>
